// src/components/ResetPassword.js
import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

function ResetPassword() {
  const { token }                 = useParams();   // /reset-password/:token
  const navigate                  = useNavigate();
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [showPw, setShowPw]       = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [done, setDone]           = useState(false);

  const strength = (() => {
    const p = password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = ["", "#e06c6c", "#e0a46c", "#6cb8e0", "#6ce083"][strength];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 6)  { setError("Password must be at least 6 characters."); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password/${token}`, { password });
      setDone(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "This link has expired or is invalid. Please request a new one.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');
        :root { --gold:#C9A84C; --gold-light:#E8C97A; --dark:#0A0A0A; }

        .auth-root {
          font-family:'Jost',sans-serif; background:var(--dark); min-height:100vh;
          display:flex; flex-direction:column; align-items:center; justify-content:center;
          position:relative; overflow:hidden; padding:6rem 1.5rem 3rem;
        }
        .auth-root::before {
          content:''; position:absolute; top:-20%; left:50%; transform:translateX(-50%);
          width:700px; height:700px;
          background:radial-gradient(ellipse,rgba(201,168,76,0.06) 0%,transparent 65%);
          pointer-events:none;
        }

        .auth-corner { position:absolute; width:80px; height:80px; pointer-events:none; }
        .auth-corner.tl { top:80px;left:24px; border-top:1px solid rgba(201,168,76,0.25); border-left:1px solid rgba(201,168,76,0.25); }
        .auth-corner.br { bottom:24px;right:24px; border-bottom:1px solid rgba(201,168,76,0.25); border-right:1px solid rgba(201,168,76,0.25); }

        .auth-card {
          position:relative; z-index:2; width:100%; max-width:440px;
          background:rgba(255,255,255,0.025); border:1px solid rgba(201,168,76,0.2);
          border-radius:4px; padding:3rem 2.5rem 2.5rem; animation:authFadeUp 0.7s ease both;
        }
        @keyframes authFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .auth-card::before { content:''; position:absolute; top:0; left:10%; right:10%; height:1px; background:linear-gradient(90deg,transparent,var(--gold),transparent); }

        .auth-eyebrow { font-size:0.62rem; letter-spacing:5px; text-transform:uppercase; color:var(--gold); text-align:center; margin-bottom:0.6rem; }
        .auth-title { font-family:'Cormorant Garamond',serif; font-size:2.3rem; font-weight:700; color:#fff; text-align:center; line-height:1.1; margin-bottom:0.3rem; }
        .auth-title em { font-style:italic; color:var(--gold-light); }
        .auth-divider { width:40px; height:1px; background:var(--gold); margin:1.2rem auto 1.5rem; opacity:0.7; }
        .auth-subtitle { font-size:0.83rem; color:rgba(255,255,255,0.4); text-align:center; margin-bottom:2rem; font-weight:300; line-height:1.75; }

        /* Done state */
        .done-box { text-align:center; padding:1rem 0 0.5rem; }
        .done-icon { font-size:2.5rem; color:var(--gold); display:block; margin-bottom:1rem; }
        .done-title { font-family:'Cormorant Garamond',serif; font-size:1.6rem; color:#fff; margin-bottom:0.6rem; }
        .done-text { font-size:0.85rem; color:rgba(255,255,255,0.5); line-height:1.75; font-weight:300; }

        .auth-error { background:rgba(220,53,69,0.1); border:1px solid rgba(220,53,69,0.3); border-radius:3px; padding:0.75rem 1rem; font-size:0.82rem; color:#f8908a; margin-bottom:1.5rem; display:flex; align-items:flex-start; gap:0.5rem; line-height:1.5; }

        .auth-field { margin-bottom:1.4rem; }
        .auth-label { display:block; font-size:0.65rem; letter-spacing:3px; text-transform:uppercase; color:rgba(255,255,255,0.4); margin-bottom:0.55rem; font-weight:500; }
        .auth-input-wrap { position:relative; }
        .auth-input { width:100%; background:rgba(255,255,255,0.04); border:1px solid rgba(201,168,76,0.18); border-radius:3px; padding:0.85rem 1rem; color:#fff; font-family:'Jost',sans-serif; font-size:0.9rem; font-weight:300; outline:none; transition:border-color 0.25s,background 0.25s; -webkit-appearance:none; }
        .auth-input::placeholder { color:rgba(255,255,255,0.2); }
        .auth-input:focus { border-color:rgba(201,168,76,0.55); background:rgba(201,168,76,0.04); }
        .auth-input.has-toggle { padding-right:3.5rem; }

        .auth-toggle { position:absolute; right:1rem; top:50%; transform:translateY(-50%); background:none; border:none; color:rgba(255,255,255,0.3); cursor:pointer; padding:0; font-size:0.72rem; letter-spacing:1px; text-transform:uppercase; font-family:'Jost',sans-serif; transition:color 0.2s; }
        .auth-toggle:hover { color:var(--gold); }

        .strength-row { display:flex; align-items:center; gap:0.6rem; margin-top:0.5rem; }
        .strength-bars { display:flex; gap:3px; flex:1; }
        .strength-bar { height:2px; flex:1; border-radius:2px; background:rgba(255,255,255,0.08); transition:background 0.3s; }
        .strength-label { font-size:0.65rem; letter-spacing:1.5px; text-transform:uppercase; min-width:40px; text-align:right; }

        .auth-match { margin-top:0.4rem; font-size:0.7rem; letter-spacing:0.5px; min-height:1rem; }

        .auth-submit { width:100%; background:var(--gold); border:none; border-radius:3px; padding:0.95rem; color:#0A0A0A; font-family:'Jost',sans-serif; font-weight:600; font-size:0.82rem; letter-spacing:3px; text-transform:uppercase; cursor:pointer; transition:background 0.3s,transform 0.2s,opacity 0.2s; margin-top:0.5rem; position:relative; overflow:hidden; }
        .auth-submit::after { content:''; position:absolute; inset:0; background:linear-gradient(90deg,transparent,rgba(255,255,255,0.14),transparent); transform:translateX(-100%); transition:transform 0.55s; }
        .auth-submit:hover::after { transform:translateX(100%); }
        .auth-submit:hover { background:var(--gold-light); transform:translateY(-1px); }
        .auth-submit:disabled { opacity:0.55; cursor:not-allowed; transform:none; }

        .auth-back { display:flex; align-items:center; justify-content:center; gap:0.4rem; margin-top:1.8rem; font-size:0.8rem; color:rgba(255,255,255,0.3); text-decoration:none; transition:color 0.2s; }
        .auth-back:hover { color:var(--gold); }

        .auth-spinner { display:inline-block; width:14px; height:14px; border:2px solid rgba(10,10,10,0.3); border-top-color:#0A0A0A; border-radius:50%; animation:spin 0.7s linear infinite; vertical-align:middle; margin-right:0.5rem; }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>

      <div className="auth-root">
        <div className="auth-corner tl" />
        <div className="auth-corner br" />

        <div className="auth-card">
          <p className="auth-eyebrow">Nairobi's Ultimate Experience</p>
          <h1 className="auth-title">New <em>Password</em></h1>
          <div className="auth-divider" />

          {done ? (
            <div className="done-box">
              <span className="done-icon">✦</span>
              <h2 className="done-title">Password Updated</h2>
              <p className="done-text">Your password has been reset successfully.<br />Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <p className="auth-subtitle">Choose a strong new password for your account.</p>
              {error && <div className="auth-error"><span>✕</span> {error}</div>}
              <form onSubmit={handleSubmit}>
                <div className="auth-field">
                  <label className="auth-label" htmlFor="rp-password">New Password</label>
                  <div className="auth-input-wrap">
                    <input id="rp-password" type={showPw ? "text" : "password"} className="auth-input has-toggle"
                      placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password" />
                    <button type="button" className="auth-toggle" onClick={() => setShowPw(v => !v)}>
                      {showPw ? "Hide" : "Show"}
                    </button>
                  </div>
                  {password && (
                    <div className="strength-row">
                      <div className="strength-bars">
                        {[1,2,3,4].map(i => (
                          <div key={i} className="strength-bar" style={{ background: i <= strength ? strengthColor : undefined }} />
                        ))}
                      </div>
                      <span className="strength-label" style={{ color: strengthColor }}>{strengthLabel}</span>
                    </div>
                  )}
                </div>

                <div className="auth-field">
                  <label className="auth-label" htmlFor="rp-confirm">Confirm New Password</label>
                  <div className="auth-input-wrap">
                    <input id="rp-confirm" type={showPw ? "text" : "password"} className="auth-input"
                      placeholder="••••••••" value={confirm} onChange={e => setConfirm(e.target.value)} required autoComplete="new-password" />
                  </div>
                  {confirm && (
                    <div className="auth-match" style={{ color: password === confirm ? "#6ce083" : "#e06c6c" }}>
                      {password === confirm ? "✓ Passwords match" : "✕ Passwords don't match"}
                    </div>
                  )}
                </div>

                <button type="submit" className="auth-submit" disabled={loading}>
                  {loading && <span className="auth-spinner" />}
                  {loading ? "Updating…" : "Update Password"}
                </button>
              </form>
            </>
          )}

          <Link to="/login" className="auth-back">← Back to Sign In</Link>
        </div>
      </div>
    </>
  );
}

export default ResetPassword;
